import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, X } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { api, formatApiError } from "@/lib/api";

const AGREEMENTS = [
  "I confirm that I am 18 years of age or older and legally eligible to use Monroe Society.",
  "I confirm that the information I have provided is accurate.",
  "I agree to use Monroe Society lawfully and respectfully.",
  "I agree not to engage in harassment, threats, coercion, trafficking, exploitation, fraud, or other unlawful conduct.",
  "I agree to Monroe Society's Terms of Service, Privacy Policy, and Client Code of Conduct.",
];

const POLICIES = [
  "You must be 18 years of age or older and legally permitted to use this service in your jurisdiction.",
  "You agree to use Monroe Society only for lawful purposes and to comply with all applicable U.S. federal, state, and local laws.",
  "Be respectful and professional toward providers, members, and Monroe Society staff.",
  "No harassment, threats, intimidation, stalking, abusive behavior, or unwanted repeated contact.",
  "No spam, scams, fraud, impersonation, or misleading information.",
  "Do not use Monroe Society to involve, solicit, exploit, or endanger anyone under the age of 18.",
  "Do not use the platform to facilitate human trafficking, coercion, exploitation, or other unlawful activity.",
  "Respect the privacy, boundaries, and decisions of other members and providers.",
  "Do not attempt to circumvent an account suspension, restriction, or ban by creating another account.",
];

const inputCls = "w-full bg-elevated border hairline-gold px-4 py-3.5 text-sm text-neutral-200 placeholder:text-neutral-600 focus:outline-none focus:border-amber-500/50 transition-colors";

export default function ClientJoin() {
  const [form, setForm] = useState({ display_name: "", email: "", password: "", confirm: "" });
  const [agreed, setAgreed] = useState(AGREEMENTS.map(() => false));
  const [verified, setVerified] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const allAgreed = agreed.every(Boolean);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) return setError("Passwords do not match.");
    if (!verified) return setError("Please complete 18+ age verification before creating your account.");
    if (!allAgreed) return setError("All required agreements must be confirmed.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", {
        email: form.email,
        password: form.password,
        display_name: form.display_name,
        role: "client",
        certifications: AGREEMENTS,
        verification_method: "age_estimation_simulated",
      });
      setDone(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthLayout eyebrow="Client Registration" title={<>Welcome to the <span className="italic gold-text">Society</span></>}>
        <div data-testid="client-success-panel" className="glass-panel p-8 sm:p-10 text-center">
          <ShieldCheck className="mx-auto text-gold mb-5" size={36} strokeWidth={1.25} />
          <h2 className="font-serif text-2xl text-amber-100/95 mb-3">Account created, {done.display_name}</h2>
          <p className="text-sm text-neutral-400 font-light leading-relaxed">
            Your account status is <span className="text-gold uppercase tracking-[0.15em] text-xs">Pending Age Verification</span>.
            Client features unlock once verification is confirmed.
          </p>
          <Link to="/" data-testid="client-success-home-link" className="inline-block mt-8 px-8 py-3 border hairline-gold text-xs uppercase tracking-[0.2em] text-amber-100/90 hover:border-amber-400/60 transition-colors">
            Return Home
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      eyebrow="Client Registration"
      title={<>Create a <span className="italic gold-text">client account</span></>}
      subtitle="Monroe Society is a U.S.-based advertising platform connecting clients with independent adult service providers. Before creating your account, please review and agree to the following."
    >
      <ul className="space-y-2.5 mb-12 p-6 bg-onyx border hairline-gold">
        {POLICIES.map((p) => (
          <li key={p} className="flex gap-3 text-sm text-neutral-400 font-light leading-relaxed">
            <span className="w-1 h-1 rotate-45 bg-gold/60 mt-2 shrink-0" />
            {p}
          </li>
        ))}
      </ul>
      <p className="text-xs text-neutral-500 leading-relaxed mb-12">
        Violations of these policies may result in removal of content, suspension, or permanent termination of your
        account, and may result in additional action when appropriate or required by law.
      </p>

      <form data-testid="client-signup-form" onSubmit={submit} className="space-y-10">
        <div className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Display Name / Username*</label>
            <input data-testid="client-display-name-input" required value={form.display_name} onChange={set("display_name")} placeholder="Enter your display name" className={inputCls} />
            <p className="mt-1.5 text-xs text-neutral-600">Your display name does not have to be your legal name.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Email Address*</label>
            <input data-testid="client-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="Enter email" className={inputCls} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Password*</label>
              <input data-testid="client-password-input" type="password" required minLength={8} value={form.password} onChange={set("password")} placeholder="Create password" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-neutral-400 mb-2">Confirm Password*</label>
              <input data-testid="client-confirm-password-input" type="password" required value={form.confirm} onChange={set("confirm")} placeholder="Confirm password" className={inputCls} />
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-8 bg-onyx border hairline-gold">
          <h3 className="font-serif text-2xl text-amber-100/95 mb-4">Age Verification</h3>
          <p className="text-sm text-neutral-400 font-light leading-relaxed mb-4">
            You must successfully complete our required 18+ age verification before accessing client features.
            Verification is completed through our designated third-party provider — depending on the available
            method, you may confirm your age through facial age estimation or government-issued identification.
            Monroe Society receives confirmation that you meet the required age threshold and does not need
            unnecessary identification information to maintain your client account.
          </p>
          <button
            type="button"
            data-testid="client-verify-age-button"
            onClick={() => setVerifyOpen(true)}
            className={`px-8 py-3 text-xs uppercase tracking-[0.2em] font-medium transition-all duration-300 ${
              verified ? "border border-emerald-700 text-emerald-400" : "bg-gold text-obsidian hover:bg-gold-bright"
            }`}
          >
            {verified ? "Age Verified" : "Verify My Age"}
          </button>
        </div>

        <div className="p-6 sm:p-8 bg-onyx border hairline-gold">
          <h3 className="font-serif text-2xl text-amber-100/95 mb-6">Required Agreements</h3>
          <div className="space-y-4">
            {AGREEMENTS.map((a, i) => (
              <label key={a} className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid={`client-agreement-checkbox-${i + 1}`}
                  checked={agreed[i]}
                  onChange={() => setAgreed(agreed.map((v, j) => (j === i ? !v : v)))}
                  className="mt-1 h-4 w-4 shrink-0 appearance-none border hairline-gold bg-elevated checked:bg-gold checked:border-gold transition-colors cursor-pointer"
                />
                <span className="text-sm text-neutral-400 font-light leading-relaxed group-hover:text-neutral-300 transition-colors">{a}</span>
              </label>
            ))}
          </div>
        </div>

        <p className="text-xs text-neutral-500 leading-relaxed">
          Account status: your account will remain <span className="text-gold/80">Pending Age Verification</span>{" "}
          until the required verification is successfully completed.
        </p>

        {error && <p data-testid="client-form-error" className="text-sm text-red-400">{error}</p>}

        <button
          type="submit"
          data-testid="client-form-submit-button"
          disabled={loading}
          className="w-full py-4 bg-gold text-obsidian text-sm uppercase tracking-[0.25em] font-medium hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] transition-all duration-300 disabled:opacity-50"
        >
          {loading ? "Creating Account…" : "Create Client Account"}
        </button>

        <p className="text-sm text-neutral-500 text-center">
          Already have an account?{" "}
          <Link to="/signin" data-testid="client-signin-link" className="text-gold hover:text-gold-bright transition-colors">Sign In</Link>
        </p>
      </form>

      <AnimatePresence>
        {verifyOpen && (
          <motion.div
            data-testid="client-verification-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center bg-obsidian/90 backdrop-blur-xl px-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setVerifyOpen(false)}
          >
            <motion.div
              className="glass-panel max-w-md w-full p-8"
              initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-5">
                <ShieldCheck className="text-gold" size={30} strokeWidth={1.25} />
                <button data-testid="client-verification-close" onClick={() => setVerifyOpen(false)} className="text-neutral-500 hover:text-gold transition-colors" aria-label="Close">
                  <X size={18} />
                </button>
              </div>
              <h3 className="font-serif text-2xl text-amber-100/95 mb-3">18+ Age Verification</h3>
              <p className="text-sm text-neutral-400 font-light leading-relaxed mb-6">
                In production, this step connects to our designated verification partner (Yoti), offering facial age
                estimation or ID-based verification — including methods that confirm your age without disclosing your
                identity, as required by Colorado SB25-201. This preview simulates a successful result.
              </p>
              <button
                data-testid="client-simulate-verification-button"
                onClick={() => { setVerified(true); setVerifyOpen(false); }}
                className="w-full py-3.5 bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-bright transition-colors"
              >
                Simulate Successful Verification
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AuthLayout>
  );
}
